import { MarkerClusterer } from "@googlemaps/markerclusterer"

let map: google.maps.Map

type PropertyPoint = {
    id: number
    lat: number
    lng: number
    price?: string
    title?: string
    url?: string
    popup?: string
}

export async function initMap(mapEl: HTMLElement) {
    if (!mapEl || mapEl.hasAttribute("data-initialized")) return
    mapEl.setAttribute("data-initialized", "")

    const { Map } = await google.maps.importLibrary("maps") as google.maps.MapsLibrary
    const { Geocoder } = await google.maps.importLibrary("geocoding") as google.maps.GeocodingLibrary
    const { AdvancedMarkerElement } = await google.maps.importLibrary("marker") as google.maps.MarkerLibrary

    const type = mapEl.dataset.map ?? "single"
    const themeRoot = mapEl.dataset.themeUri ?? ""

    // 👇 DŮLEŽITÉ: načti mapId z data-map-id
    const mapId = (mapEl.dataset.mapId ?? "").trim()

    // 👇 když máš mapId, nedávej styles (cloud styling má prioritu)
    const mapOptions: google.maps.MapOptions = {
        mapTypeControl: false,
        fullscreenControl: false,
        streetViewControl: false,
        ...(mapId ? { mapId } : { styles: mapStyle }),
    }

    // --- SINGLE ---
    if (type === "single") {
        let location: google.maps.LatLng | null = null

        const address = mapEl.dataset.address ?? null
        if (address) {
            const geocoder = new Geocoder()
            await geocoder.geocode({ address }, (results, status) => {
                if (status !== "OK") return
                location = results[0]?.geometry.location ?? null
            })
        } else {
            const lat = mapEl.dataset.lat ?? null
            const lng = mapEl.dataset.lng ?? null
            if (lat && lng) location = new google.maps.LatLng(parseFloat(lat), parseFloat(lng))
        }

        if (!location) return

        mapOptions.disableDefaultUI = false
        mapOptions.center = location
        mapOptions.zoom = parseInt(mapEl.dataset.zoom ?? "14")

        map = new Map(mapEl, mapOptions)

        // může být klasický marker – nepotřebuje AdvancedMarker
        new google.maps.Marker({
            position: mapOptions.center!,
            clickable: false,
            icon: {
                url: `${themeRoot}/resources/images/pin.svg`,
                scaledSize: new google.maps.Size(75, 75),
            },
            map,
        })

        map.addListener("tilesloaded", () => mapEl.classList.add("is-loaded"))
        return
    }

    // --- PROPERTIES (ARCHIVE) ---
    // --- PROPERTIES (ARCHIVE) ---
    if (type === "properties") {
        const pointsEl = mapEl.querySelector(".Map-points") as HTMLScriptElement | null
        let points: PropertyPoint[] = pointsEl?.textContent ? JSON.parse(pointsEl.textContent) : []

        mapOptions.disableDefaultUI = false

        // pro jistotu center/zoom (fitBounds přepíše)
        mapOptions.center = points.length ? { lat: Number(points[0].lat), lng: Number(points[0].lng) } : { lat: 49.8175, lng: 15.473 }
        mapOptions.zoom = points.length ? 12 : 7

        map = new Map(mapEl, mapOptions)

        const info = new google.maps.InfoWindow({
            shouldFocus: false,
        })

        let closeTimer: number | null = null
        let activeMarkerEl: HTMLElement | null = null
        let clusterer: MarkerClusterer | null = null

        const cancelClose = () => {
            if (closeTimer) window.clearTimeout(closeTimer)
            closeTimer = null
        }

        const setActiveMarker = (el: HTMLElement | null) => {
            activeMarkerEl?.classList.remove("is-popupOpen")
            activeMarkerEl = el
            activeMarkerEl?.classList.add("is-popupOpen")
        }

        const closePopup = () => {
            cancelClose()
            setActiveMarker(null)
            info.close()
        }

        const scheduleClose = () => {
            cancelClose()
            closeTimer = window.setTimeout(() => closePopup(), 250)
        }

        google.maps.event.addListener(info, "domready", () => {
            const iw = document.querySelector(".gm-style-iw") as HTMLElement | null
            if (!iw) return
            iw.addEventListener("mouseenter", cancelClose)
            iw.addEventListener("mouseleave", scheduleClose)
        })

        const makePriceEl = (p: PropertyPoint) => {
            const el = document.createElement("div")
            el.className = "MapPriceMarker"
            el.setAttribute("aria-label", p.title || "Nemovitost")

            const text = document.createElement("span")
            text.className = "MapPriceMarker-text"
            text.textContent = (p.price && p.price.trim() !== "") ? p.price : "Detail"

            el.appendChild(text)
            return el
        }

        const updateMarkers = (newPoints: PropertyPoint[]) => {
            if (clusterer) {
                clusterer.clearMarkers()
            }

            if (!newPoints || newPoints.length === 0) return

            const bounds = new google.maps.LatLngBounds()
            const markers = newPoints
                .filter(p => p?.lat && p?.lng)
                .map(p => {
                    bounds.extend(new google.maps.LatLng(Number(p.lat), Number(p.lng)))
                    const content = makePriceEl(p)

                    const marker = new AdvancedMarkerElement({
                        position: { lat: Number(p.lat), lng: Number(p.lng) },
                        content,
                        title: p.title ?? "",
                        zIndex: 1,
                    })

                    const openPopup = () => {
                        cancelClose()
                        setActiveMarker(content)
                        info.setContent(p.popup ?? "")
                        info.open({ map, anchor: marker })
                    }

                    content.addEventListener("mouseenter", openPopup)
                    content.addEventListener("mouseleave", scheduleClose)
                    content.addEventListener("click", (e) => {
                        e.preventDefault()
                        openPopup()
                    })

                    return marker
                })

            clusterer = new MarkerClusterer({
                map,
                markers,
                renderer: {
                    render({ count, position }) {
                        const el = document.createElement("div")
                        el.className = "MapCluster"
                        el.textContent = String(count)

                        return new AdvancedMarkerElement({
                            position,
                            content: el,
                            zIndex: 1000 + count,
                        })
                    },
                },
            })

            clusterer.addListener("click", (event: any) => {
                closePopup()
                if (event?.bounds) {
                    map.fitBounds(event.bounds)
                    map.setZoom((map.getZoom() ?? 12) + 1)
                }
            })

            map.fitBounds(bounds, 60)
        }

        // Initial update
        updateMarkers(points)

        // Listen for external updates
        mapEl.addEventListener("map:update-points", (e: any) => {
            updateMarkers(e.detail?.points ?? [])
        })

        map.addListener("click", () => closePopup())
        map.addListener("tilesloaded", () => mapEl.classList.add("is-loaded"))
        return
    }

}

const mapStyle = [
    {
        "featureType": "administrative",
        "elementType": "all",
        "stylers": [
            {
                "visibility": "simplified"
            }
        ]
    },
    {
        "featureType": "landscape",
        "elementType": "geometry",
        "stylers": [
            {
                "visibility": "simplified"
            },
            {
                "color": "#fcfcfc"
            }
        ]
    },
    {
        "featureType": "poi",
        "elementType": "geometry",
        "stylers": [
            {
                "visibility": "simplified"
            },
            {
                "color": "#fcfcfc"
            }
        ]
    },
    {
        "featureType": "road.highway",
        "elementType": "geometry",
        "stylers": [
            {
                "visibility": "simplified"
            },
            {
                "color": "#dddddd"
            }
        ]
    },
    {
        "featureType": "road.arterial",
        "elementType": "geometry",
        "stylers": [
            {
                "visibility": "simplified"
            },
            {
                "color": "#dddddd"
            }
        ]
    },
    {
        "featureType": "road.local",
        "elementType": "geometry",
        "stylers": [
            {
                "visibility": "simplified"
            },
            {
                "color": "#eeeeee"
            }
        ]
    },
    {
        "featureType": "water",
        "elementType": "geometry",
        "stylers": [
            {
                "visibility": "simplified"
            },
            {
                "color": "#dddddd"
            }
        ]
    }
]
